/**
 * La aritmética del calendario (spec 015).
 *
 * POR QUÉ ESTO ES UN FICHERO Y NO TRES TROZOS DENTRO DE LAS PANTALLAS
 * ──────────────────────────────────────────────────────────────────
 * Hasta la 015, saber dónde va una cita se calculaba en tres sitios —la fila
 * donde caía, el arrastre y el formulario— con la misma regla escrita tres
 * veces. Con semana, mes y el resaltado al pinchar habría seis.
 *
 * Y el fallo de que dos de esas copias redondeen distinto **no da error**: da
 * una cita a las 10:30 que se dibuja a las 10:00. Se ve semanas después, en el
 * mostrador, cuando alguien llega y no hay hueco.
 *
 * Aquí no entra React a propósito: es aritmética, se prueba sola, y las pruebas
 * incluyen los casos feos —45 minutos, cruces de hora, y citas que empiezan
 * antes de abrir—.
 *
 * LA UNIDAD ES EL MINUTO, Y LOS PÍXELES SE DERIVAN
 * ───────────────────────────────────────────────
 * Nunca al revés. Guardar posiciones en píxeles obliga a re-calcularlo todo
 * cuando cambia el zoom, y a redondear dos veces: una al pintar y otra al leer.
 */

/** Cuántos píxeles mide una hora. Es lo único que se toca para hacer zoom.
 *  80: en 30 minutos caben las cuatro líneas del bocadillo (015 · FR-031);
 *  si alguna no entra, el hover expande — no se pinta por fuera. */
export const PX_POR_HORA = 80

/** A qué se pega el ratón al crear. Cuarto de hora: lo que usa un mostrador. */
export const PASO_MIN = 15

export type FranjaDia = { start: string; end: string; breaks: { start: string; end: string }[] } | null
export type DiaSemana =
    | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'
export type PlanSemanal = Record<DiaSemana, FranjaDia>

const DIAS: DiaSemana[] = [
    'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
]

/** El horario que se pinta cuando no se pudo leer el del negocio. Se AVISA. */
export const HORARIO_DE_EMERGENCIA = { desdeMin: 8 * 60, hastaMin: 21 * 60 }

// ── Horas ─────────────────────────────────────────────────────────────────────

/** `"09:30"` o `"09:30:00"` → 570. Devuelve `null` si no es una hora. */
export function aMinutos(hhmm: string | null | undefined): number | null {
    if (!hhmm) return null
    const m = /^(\d{1,2}):(\d{2})/.exec(hhmm)
    if (!m) return null
    const h = Number(m[1])
    const min = Number(m[2])
    if (h > 23 || min > 59) return null
    return h * 60 + min
}

/** 570 → `"09:30"`. Los minutos fuera del día se recortan al día. */
export function aHhmm(total: number): string {
    const t = Math.max(0, Math.min(24 * 60 - 1, Math.round(total)))
    return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`
}

/** Qué día de la semana es un `YYYY-MM-DD`, en los nombres que usa la agenda. */
export function diaSemanaDe(iso: string): DiaSemana {
    // Mediodía UTC: a las 00:00 un huso al oeste devolvería el día anterior.
    const d = new Date(`${iso}T12:00:00Z`)
    // `getUTCDay()` da 0 el domingo; aquí la semana empieza en lunes.
    return DIAS[(d.getUTCDay() + 6) % 7] as DiaSemana
}

// ── El horario visible del día ────────────────────────────────────────────────

export interface HorarioVisible {
    desdeMin: number
    hastaMin: number
    /** `false` cuando se está usando el horario de emergencia: hay que decirlo. */
    delNegocio: boolean
}

/**
 * De qué hora a qué hora se pinta un día (FR-002).
 *
 * Se toma la apertura más temprana y el cierre más tardío **de los profesionales
 * que trabajan ese día**, no del negocio en abstracto: si el sábado solo entra
 * una persona de 10 a 14, el sábado se pinta de 10 a 14 y no de 9 a 20 con seis
 * horas en blanco.
 *
 * `citas` entra para que una cita fuera de horario **ensanche** la vista en
 * lugar de quedarse fuera. Una cita a las 7:30 en un negocio que abre a las 9 es
 * un error, y un error hay que poder verlo; esconderlo es peor que enseñarlo
 * torcido.
 */
export function horarioVisible(
    planes: PlanSemanal[],
    dia: string,
    citas: { inicio: string; minutos: number }[] = [],
): HorarioVisible {
    const clave = diaSemanaDe(dia)
    let desde: number | null = null
    let hasta: number | null = null

    for (const plan of planes) {
        const franja = plan?.[clave]
        if (!franja) continue
        const a = aMinutos(franja.start)
        const b = aMinutos(franja.end)
        if (a === null || b === null || b <= a) continue
        desde = desde === null ? a : Math.min(desde, a)
        hasta = hasta === null ? b : Math.max(hasta, b)
    }

    const delNegocio = desde !== null && hasta !== null
    let ini = delNegocio ? (desde as number) : HORARIO_DE_EMERGENCIA.desdeMin
    let fin = delNegocio ? (hasta as number) : HORARIO_DE_EMERGENCIA.hastaMin

    for (const c of citas) {
        const a = aMinutos(c.inicio)
        if (a === null) continue
        const b = a + Math.max(0, c.minutos)
        if (a < ini) ini = a
        if (b > fin) fin = b
    }

    // A hora en punto hacia fuera: una rejilla que empieza a las 8:47 no se lee.
    ini = Math.floor(ini / 60) * 60
    fin = Math.ceil(fin / 60) * 60
    if (fin <= ini) fin = ini + 60

    return { desdeMin: ini, hastaMin: fin, delNegocio }
}

/**
 * Un horario que sirve para VARIOS días a la vez (FR-008).
 *
 * La semana necesita **una sola** franja de horas para los siete días: si cada
 * columna empezara a su hora, las rayas no cuadrarían entre columnas y la
 * pantalla dejaría de leerse de un vistazo, que es justo para lo que sirve una
 * semana.
 *
 * Se toma la unión: de la apertura más temprana de cualquier día al cierre más
 * tardío de cualquier día. Un sábado corto se ve corto porque su columna estará
 * vacía arriba y abajo, no porque tenga su propia rejilla.
 */
export function horarioDeVariosDias(
    planes: PlanSemanal[],
    dias: string[],
    citasPorDia: Record<string, { inicio: string; minutos: number }[]> = {},
): HorarioVisible {
    const cada = dias.map((d) => horarioVisible(planes, d, citasPorDia[d] ?? []))
    // Solo cuentan los días que de verdad se trabajan. Si no, un domingo cerrado
    // arrastraría el horario de emergencia (8–21) a toda la semana y estiraría
    // la rejilla con seis horas en blanco que no son de nadie.
    const reales = cada.filter((h) => h.delNegocio)
    const base = reales.length > 0 ? reales : cada

    let desdeMin = Math.min(...base.map((h) => h.desdeMin))
    let hastaMin = Math.max(...base.map((h) => h.hastaMin))

    /*
     * ⚠️ Y DESPUÉS, LAS CITAS DE **TODOS** LOS DÍAS, TRABAJEN O NO.
     *
     * Arriba solo cuentan los días que se trabajan, para que un domingo cerrado
     * no arrastre el horario de emergencia a toda la semana. Pero eso dejaba
     * fuera **las citas de ese domingo**: quedaban por debajo del rango pintado
     * y las recortaba el `overflow-hidden` de la rejilla.
     *
     * Una cita desaparecida de la pantalla es el peor fallo posible aquí, y es
     * mudo. Es el mismo FR-005 que en la vista de día: una cita fuera de horario
     * ENSANCHA la vista, nunca se esconde.
     */
    for (const citas of Object.values(citasPorDia)) {
        for (const c of citas) {
            const a = aMinutos(c.inicio)
            if (a === null) continue
            const b = a + Math.max(0, c.minutos)
            if (a < desdeMin) desdeMin = Math.floor(a / 60) * 60
            if (b > hastaMin) hastaMin = Math.ceil(b / 60) * 60
        }
    }

    return { desdeMin, hastaMin, delNegocio: reales.length > 0 }
}

/** Las horas en punto que llevan raya y etiqueta. */
export function horasDe(h: HorarioVisible): number[] {
    const out: number[] = []
    for (let m = h.desdeMin; m <= h.hastaMin; m += 60) out.push(m)
    return out
}

/** Alto total de la rejilla, en píxeles. */
export function altoDe(h: HorarioVisible): number {
    return ((h.hastaMin - h.desdeMin) / 60) * PX_POR_HORA
}

// ── Cajas ─────────────────────────────────────────────────────────────────────

export interface Caja {
    /** Píxeles desde arriba de la rejilla. */
    arriba: number
    /** Alto en píxeles. Nunca menor que el mínimo legible. */
    alto: number
}

/**
 * Dónde va una cita, y cuánto mide (FR-001).
 *
 * `ALTO_MINIMO` existe porque una cita de 5 minutos mediría 4,6 px y no se
 * podría ni pinchar. Se dibuja más alta de lo que dura **a sabiendas**: es la
 * única mentira de este fichero y es preferible a una caja invisible. Solo
 * afecta a duraciones por debajo de ~15 min.
 */
const ALTO_MINIMO = 14

export function cajaDe(inicioHhmm: string, minutos: number, h: HorarioVisible): Caja | null {
    const ini = aMinutos(inicioHhmm)
    if (ini === null) return null
    const arriba = ((ini - h.desdeMin) / 60) * PX_POR_HORA
    const alto = Math.max(ALTO_MINIMO, (Math.max(0, minutos) / 60) * PX_POR_HORA)
    return { arriba, alto }
}

/** El minuto al que corresponde una posición del ratón dentro de la rejilla. */
export function minutoDeY(y: number, h: HorarioVisible): number {
    const crudo = h.desdeMin + (y / PX_POR_HORA) * 60
    const pegado = Math.round(crudo / PASO_MIN) * PASO_MIN
    // Sin el tope, arrastrar por debajo del último píxel propone una cita que
    // empieza después del cierre — y la agenda la aceptaría.
    return Math.max(h.desdeMin, Math.min(h.hastaMin - PASO_MIN, pegado))
}

// ── Solapes ───────────────────────────────────────────────────────────────────

export interface ConSolape<T> {
    cita: T
    /** En qué carril va, de 0 a `carriles - 1`. */
    carril: number
    /** Cuántos carriles hacen falta en su grupo. */
    carriles: number
}

/**
 * Reparte en carriles las citas que se pisan.
 *
 * Sin esto, dos citas a la misma hora se dibujan una encima de otra y la de
 * abajo no existe para quien mira. Y eso importa más aquí que en una agenda
 * normal: en la disposición «todos juntos» **es lo habitual** que dos
 * profesionales tengan cita a la misma hora.
 *
 * El reparto es por GRUPO de solape encadenado, no por par: si A pisa a B y B
 * pisa a C sin que A toque a C, los tres comparten anchura. Repartir por pares
 * dejaría a C encima de A.
 */
export function repartirSolapes<T extends { inicio: string; minutos: number }>(
    citas: T[],
): ConSolape<T>[] {
    const conRango = citas
        .map((c) => ({ c, ini: aMinutos(c.inicio) ?? 0, fin: (aMinutos(c.inicio) ?? 0) + Math.max(1, c.minutos) }))
        .sort((a, b) => a.ini - b.ini || a.fin - b.fin)

    const salida: ConSolape<T>[] = []
    let grupo: typeof conRango = []
    let finGrupo = -1

    const cerrar = () => {
        if (grupo.length === 0) return
        // Cada uno al primer carril libre. `finPorCarril` guarda hasta cuándo
        // está ocupado cada carril.
        const finPorCarril: number[] = []
        const asignado: number[] = []
        for (const g of grupo) {
            let carril = finPorCarril.findIndex((f) => f <= g.ini)
            if (carril === -1) {
                carril = finPorCarril.length
                finPorCarril.push(g.fin)
            } else {
                finPorCarril[carril] = g.fin
            }
            asignado.push(carril)
        }
        const carriles = finPorCarril.length
        grupo.forEach((g, i) => {
            salida.push({ cita: g.c, carril: asignado[i] as number, carriles })
        })
        grupo = []
        finGrupo = -1
    }

    for (const r of conRango) {
        if (grupo.length > 0 && r.ini >= finGrupo) cerrar()
        grupo.push(r)
        finGrupo = Math.max(finGrupo, r.fin)
    }
    cerrar()
    return salida
}

// ── Rangos ────────────────────────────────────────────────────────────────────

/** Suma días a un `YYYY-MM-DD` sin tocar husos. */
export function sumarDias(dia: string, n: number): string {
    const d = new Date(`${dia}T00:00:00Z`)
    d.setUTCDate(d.getUTCDate() + n)
    return d.toISOString().slice(0, 10) as string
}

/** El lunes de la semana de un día. */
export function lunesDe(dia: string): string {
    const d = new Date(`${dia}T12:00:00Z`)
    return sumarDias(dia, -((d.getUTCDay() + 6) % 7))
}

/** Los siete días de la semana que contiene `dia`. */
export function semanaDe(dia: string): string[] {
    const l = lunesDe(dia)
    return Array.from({ length: 7 }, (_, i) => sumarDias(l, i))
}

/**
 * La cuadrícula del mes: siempre 6 filas de 7, empezando en lunes.
 *
 * Seis y no «las que hagan falta» a propósito: un mes que a veces mide 5 filas y
 * a veces 6 hace que la página salte de alto al cambiar de mes, y que los días
 * de la semana caigan a distinta altura cada vez.
 */
export function mesDe(dia: string): string[] {
    const d = new Date(`${dia}T12:00:00Z`)
    const primero = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`
    const inicio = lunesDe(primero)
    return Array.from({ length: 42 }, (_, i) => sumarDias(inicio, i))
}

/** ¿Es `dia` del mismo mes que `referencia`? Para apagar los días de relleno. */
export function mismoMes(dia: string, referencia: string): boolean {
    return dia.slice(0, 7) === referencia.slice(0, 7)
}

// ── Huecos libres al mover ────────────────────────────────────────────────────

export interface IntervaloMin {
    desdeMin: number
    hastaMin: number
}

/**
 * En qué tramos trabaja un profesional ese día, ya sin descansos.
 *
 * Vacío = no trabaja (o el plan no se entiende). No se inventa un 8–21: eso
 * ofrecería huecos que la agenda rechazaría.
 */
export function jornadaDe(plan: PlanSemanal | undefined, dia: string): IntervaloMin[] {
    const franja = plan?.[diaSemanaDe(dia)]
    if (!franja) return []
    const a = aMinutos(franja.start)
    const b = aMinutos(franja.end)
    if (a === null || b === null || b <= a) return []
    const descansos: IntervaloMin[] = []
    for (const br of franja.breaks ?? []) {
        const x = aMinutos(br.start)
        const y = aMinutos(br.end)
        if (x === null || y === null || y <= x) continue
        descansos.push({ desdeMin: x, hastaMin: y })
    }
    return restarIntervalos([{ desdeMin: a, hastaMin: b }], descansos)
}

/** Lo que queda de `base` cuando se le quitan `quita`. */
export function restarIntervalos(base: IntervaloMin[], quita: IntervaloMin[]): IntervaloMin[] {
    let out = base.filter((b) => b.hastaMin > b.desdeMin)
    for (const h of quita) {
        if (h.hastaMin <= h.desdeMin) continue
        const next: IntervaloMin[] = []
        for (const b of out) {
            if (h.hastaMin <= b.desdeMin || h.desdeMin >= b.hastaMin) {
                next.push(b)
                continue
            }
            if (h.desdeMin > b.desdeMin) next.push({ desdeMin: b.desdeMin, hastaMin: h.desdeMin })
            if (h.hastaMin < b.hastaMin) next.push({ desdeMin: h.hastaMin, hastaMin: b.hastaMin })
        }
        out = next
    }
    return out
}

/** Huecos de la jornada que no pisan ninguna cita. */
export function huecosLibres(jornada: IntervaloMin[], ocupadas: IntervaloMin[]): IntervaloMin[] {
    return restarIntervalos(jornada, ocupadas)
}

/**
 * Horas a las que cabe una cita de `duracion` dentro del hueco.
 *
 * El paso es el del mostrador (`PASO_MIN`). Un hueco más corto que la cita
 * no ofrece nada: no se inventa un sitio que no cabe.
 */
export function iniciosQueCaben(hueco: IntervaloMin, duracion: number, paso = PASO_MIN): number[] {
    if (duracion <= 0 || paso <= 0 || hueco.hastaMin - hueco.desdeMin < duracion) return []
    const primero = Math.ceil(hueco.desdeMin / paso) * paso
    const out: number[] = []
    for (let t = primero; t + duracion <= hueco.hastaMin; t += paso) out.push(t)
    return out
}
